import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useUserStatusCheck } from '../hooks/useUserStatusCheck';
import { AppLayout } from '../components/AppLayout';
import { Icon } from '../components/Icon';
import { ProjectService } from '../services/projectService';
import { Project } from '../types';
import '../styles/DashboardPage.css';

export const DashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [recentProjects, setRecentProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);

  // Poll for user status changes every 10 seconds
  useUserStatusCheck({ checkInterval: 10000 });

  useEffect(() => {
    loadRecentProjects();
  }, []);

  const loadRecentProjects = async () => {
    try {
      setLoading(true);
      const projects = await ProjectService.getAllProjects();
      // Get the 5 most recent projects
      setRecentProjects(projects.slice(0, 5));
    } catch (error) {
      console.error('Failed to load recent projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date: Date) => {
    // Format date without timezone conversion
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      timeZone: 'UTC', // Force UTC to prevent timezone shifts
    });
  };

  const getStatusColor = (status: string, isExpired?: boolean) => {
    if (isExpired) return '#dc2626';
    switch (status) {
      case 'draft': return '#64748b';
      case 'active': return '#059669';
      case 'archived': return '#7c3aed';
      default: return '#3b82f6';
    }
  };

  const getDaysUntilExpiration = (expirationDate?: Date) => {
    if (!expirationDate) return null;
    const now = new Date();
    const diffTime = expirationDate.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  return (
    <AppLayout>
      <div className="welcome-section">
        <h2>Welcome back, {user?.name}!</h2>
        <p>Start by creating a new quote or managing your materials</p>
      </div>

      <div className="quick-actions">
        <div className="action-card">
          <h3 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Icon name="document" size={22} /> Create Quote
          </h3>
          <p>Start a new quote for your customer</p>
          <button onClick={() => navigate('/quotes/new')} className="action-button">
            New Quote
          </button>
        </div>

        <div className="action-card">
          <h3 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Icon name="chart" size={22} /> View All Quotes
          </h3>
          <p>Browse and manage all your quotes</p>
          <button onClick={() => navigate('/quotes')} className="action-button">
            View Quotes
          </button>
        </div>

        {isAdmin && (
          <div className="action-card">
            <h3 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Icon name="package" size={22} /> Add Material
            </h3>
            <p>Add new materials to your inventory</p>
            <button onClick={() => navigate('/admin/materials')} className="action-button">
              Manage Materials
            </button>
          </div>
        )}
      </div>

      <div className="recent-quotes-section">
        <div className="section-header">
          <h3>Recent Quotes</h3>
          <button onClick={() => navigate('/quotes')} className="view-all-button">
            View All →
          </button>
        </div>

        {loading ? (
          <div className="loading-text">Loading recent quotes...</div>
        ) : recentProjects.length === 0 ? (
          <div className="empty-recent">
            <p>No quotes yet. Create your first quote to get started!</p>
          </div>
        ) : (
          <div className="recent-quotes-list">
            {recentProjects.map((project) => {
              const daysUntilExpiration = getDaysUntilExpiration(project.expirationDate);
              const isExpiringSoon = daysUntilExpiration !== null && daysUntilExpiration > 0 && daysUntilExpiration <= 7;

              return (
                <div
                  key={project.id}
                  className={`recent-quote-item ${project.isExpired ? 'expired' : ''}`}
                  onClick={() => navigate(`/quotes/${project.id}`)}
                >
                  <div className="recent-quote-main">
                    <div className="recent-quote-title">{project.name}</div>
                    <div className="recent-quote-client">{project.clientName}</div>
                  </div>
                  <div className="recent-quote-meta">
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                      <span
                        className="recent-status-badge"
                        style={{ backgroundColor: getStatusColor(project.status, project.isExpired) }}
                      >
                        {project.isExpired ? 'Expired' : project.status}
                      </span>
                      {isExpiringSoon && !project.isExpired && (
                        <span
                          className="recent-status-badge warning-badge"
                          style={{ backgroundColor: '#f59e0b', fontSize: '10px', display: 'flex', alignItems: 'center', gap: '4px' }}
                        >
                          <Icon name="alert" size={12} color="#ffffff" /> {daysUntilExpiration}d left
                        </span>
                      )}
                    </div>
                    <span className="recent-quote-date">{formatDate(project.updatedAt)}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="info-section">
        <h3>Getting Started</h3>
        <ul>
          <li>Create a new quote from the Quotes section</li>
          <li>Add materials and processes to build your quote</li>
          <li>Apply modifiers for custom work</li>
          <li>Export quotes as PDF or print</li>
          {isAdmin && <li>Use Settings to customize branding and pricing</li>}
        </ul>
      </div>
    </AppLayout>
  );
};
